import api from './api';

export const settingsService = {
  /**
   * Update user notification preferences
   * @param {object} preferences Notification preferences object
   * @param {boolean} preferences.emailNotifications Email notification preference
   * @returns {Promise<object>} Updated user data with message
   */
  async updateNotificationPreferences(preferences) {
    const response = await api.put('/settings/notifications', preferences);
    // Backend returns: { success: true, message: "...", data: { emailNotifications: boolean } }
    return {
      ...response.data.data,
      message: response.data.message, // Include success message from backend
    };
  },

  /**
   * Get user notification preferences
   * @returns {Promise<object>} User notification preferences
   */
  async getNotificationPreferences() {
    const response = await api.get('/settings/notifications');
    // Backend returns: { success: true, data: { emailNotifications: boolean } }
    return response.data.data;
  },

  /**
   * Update user personal details/profile
   * @param {object} profileData Personal details object
   * @param {string} profileData.name User's full name
   * @param {string} profileData.email User's email
   * @param {string} profileData.phone User's phone number
   * @param {string} profileData.designation User's designation
   * @param {string} profileData.department User's department (stored in a custom field if needed)
   * @param {string} userId Optional user ID - if not provided, will try to get from localStorage
   * @returns {Promise<object>} Updated user data with message
   */
  async updatePersonalDetails(profileData, userId = null) {
    // Send all fields including phone, whatsappNumber, and department
    const backendData = {
      name: profileData.name,
      email: profileData.email,
      phone: profileData.phone, // Send phone field
      whatsappNumber: profileData.whatsappNumber || profileData.phone, // Send whatsappNumber, fallback to phone if not provided
      designation: profileData.designation,
      department: profileData.department, // Include department (backend can handle it even if not in model)
    };

    // Try /auth/me first (common pattern for updating current user)
    try {
      const response = await api.put('/auth/me', backendData);
      // Handle different possible response structures
      const userData = response.data.data?.user || response.data.data || response.data;
      return {
        ...userData,
        message: response.data.message || 'Personal details updated successfully',
      };
    } catch (error) {
      // If /auth/me doesn't exist (404), try /admin/users/:userId
      if (error.response?.status === 404) {
        try {
          // Get user ID from parameter or localStorage
          let targetUserId = userId;
          if (!targetUserId) {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            targetUserId = user._id || user.id;
          }
          
          if (targetUserId) {
            const response = await api.put(`/admin/users/${targetUserId}`, backendData);
            const userData = response.data.data?.user || response.data.data || response.data;
            return {
              ...userData,
              message: response.data.message || 'Personal details updated successfully',
            };
          } else {
            throw new Error('User ID not found. Please log in again.');
          }
        } catch (adminError) {
          // If admin endpoint also fails, throw with helpful message
          if (adminError.response?.status === 404) {
            throw new Error('Profile update endpoint not found. The backend needs to implement either PUT /api/auth/me or PUT /api/admin/users/:userId endpoint.');
          }
          throw adminError;
        }
      }
      // If error is not 404, rethrow it
      throw error;
    }
  },
};

